// React
import React from "react";

// MUI
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
  Chip,
  Stack,
  Divider,
  IconButton,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";

// Icons

import {
  OpenInBrowser,
  CloseRounded,
  CalendarMonthRounded,
  EmailRounded,
  LinkedIn,
  ReviewsRounded,
} from "@mui/icons-material";

// Project
import themes from "config/themes";
import SigilUiGridButton from "UIComponents/SigilUiGridButton";
import { useSigilLinks } from "hooks/useSigilLinks";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(1.5),
    boxShadow: theme.shadows[8],
    maxWidth: 640,
  },
}));

const Row = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "auto 1fr auto",
  gap: theme.spacing(1.5),
  alignItems: "center",
  padding: theme.spacing(1.25, 1.5),
  borderRadius: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.5),
}));

function CreditChip({ amount }: { amount: number }) {
  return (
    <Chip
      label={`+${amount} credits`}
      size="small"
      sx={{ fontWeight: 700 }}
      color="default"
      variant="outlined"
    />
  );
}

interface FreeCreditsModalProps {
  open: boolean;
  onClose: () => void;
}

const FreeCreditsModal: React.FC<FreeCreditsModalProps> = ({
  open,
  onClose,
}) => {
  const { links, mailto } = useSigilLinks();

  const handleClose = () => onClose();

  const handlePrimaryClick = (url: string) => () => {
    if (url.startsWith("mailto:")) {
      window.location.href = url;
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const AccentIconWrap = styled(Box)(() => ({
    width: 36,
    height: 36,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    backgroundColor: alpha(themes.palette.accent.main, 0.15),
    border: `1px solid ${alpha(themes.palette.accent.main, 0.35)}`,
    color: themes.palette.accent.dark,
  }));

  const items = [
    {
      icon: (
        <AccentIconWrap>
          <ReviewsRounded fontSize="small" />
        </AccentIconWrap>
      ),
      title: "Leave a Google review",
      sub: "Share a quick note about your experience with Sigil.",
      credits: 2,
      primary: {
        label: "Open Google",
        href: links.googleReview,
      },
      secondary: null as null | React.ReactNode,
    },
    {
      icon: (
        <AccentIconWrap>
          <LinkedIn fontSize="small" />
        </AccentIconWrap>
      ),
      title: "Tag Sigil Inc on LinkedIn",
      sub: "Tell your network how enforcement helps your brand.",
      credits: 4,
      primary: {
        label: "Open LinkedIn",
        href: links.linkedIn,
      },
      secondary: null,
    },
    {
      icon: (
        <AccentIconWrap>
          <EmailRounded fontSize="small" />
        </AccentIconWrap>
      ),
      title: "Email a quick testimonial",
      sub: links.supportEmail,
      credits: 4,
      primary: {
        label: "Email Sigil",
        href: mailto(links.supportEmail),
      },
      secondary: (
        <Link href={mailto(links.supportEmail)} underline="hover">
          {links.supportEmail}
        </Link>
      ),
    },
    {
      icon: (
        <AccentIconWrap>
          <CalendarMonthRounded fontSize="small" />
        </AccentIconWrap>
      ),
      title: "Join a short case-study call",
      sub: "15–20 minutes with our team.",
      credits: 6,
      primary: {
        label: "Choose a time",
        href: links.calendar,
      },
      secondary: null,
    },
  ];

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6, fontWeight: 700, fontSize: "1.5rem" }}>
        Get free enforcement credits
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Earn credits by helping other brands discover Sigil. Choose any option
          below. When you’re done, we’ll add the credits to your account.
        </Typography>

        <Stack spacing={1.25}>
          {items.map((it, idx) => (
            <Row key={idx}>
              {it.icon}
              <Box sx={{ minWidth: 0 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 0.25 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {it.title}
                  </Typography>
                  <CreditChip amount={it.credits} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {it.sub}
                </Typography>
              </Box>
              <SigilUiGridButton
                eventLabel={`free_credits_modal_${it.primary.label}`}
                label={it.primary.label}
                onClick={handlePrimaryClick(it.primary.href)}
                endIcon={<OpenInBrowser />}
                sx={{
                  width: "100%",
                  minWidth: 164,
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                  fontWeight: 700,
                }}
              />
            </Row>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary">
          <strong>How it works:</strong> Complete any action and we’ll verify it
          (review/post/email/call) and apply the credits within a couple of
          business days. Already did one of these? Just let us know and we’ll
          retro-credit.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>
        <SigilUiGridButton
          onClick={handlePrimaryClick(mailto(links.supportEmail))}
          eventLabel="free_credits_modal_close"
          label="Email Sigil"
          sx={{
            fontWeight: 700,
          }}
        />
      </DialogActions>
    </StyledDialog>
  );
};

export default FreeCreditsModal;
